/**
 * Helpers for Event Bulk Application Entry Volume Discounts (Client-side).
 */

export const BULK_DISCOUNT_MODES = {
  INCREMENTAL: 'INCREMENTAL',
  RETROACTIVE: 'RETROACTIVE',
};

export const RETROACTIVE_BILLING_TREATMENTS = {
  UNBILLED_ONLY: 'UNBILLED_ONLY',
  REVISE_PROFORMA: 'REVISE_PROFORMA',
  FORWARD_OFFSET: 'FORWARD_OFFSET',
};

export function normalizeBulkDiscountTiers(tiers) {
  if (!Array.isArray(tiers)) return [];
  return tiers
    .filter((t) => t && Number(t.minEntries) >= 1 && (Number(t.discountPercent) > 0 || Number(t.discountAmount) > 0 || Number(t.unitPrice) > 0))
    .map((t) => {
      const type = t.discountType || (Number(t.discountAmount) > 0 ? 'FIXED_AMOUNT' : (Number(t.unitPrice) > 0 ? 'FIXED_PRICE' : 'PERCENTAGE'));
      return {
        ...(t._id ? { _id: t._id } : {}),
        minEntries: Math.max(1, Math.floor(Number(t.minEntries))),
        maxEntries:
          t.maxEntries != null && t.maxEntries !== '' && !Number.isNaN(Number(t.maxEntries))
            ? Math.max(Math.floor(Number(t.minEntries)), Math.floor(Number(t.maxEntries)))
            : null,
        discountType: type,
        discountPercent: Number(t.discountPercent) > 0 ? Math.min(100, Math.max(0.01, Math.round(Number(t.discountPercent) * 100) / 100)) : 0,
        discountAmount: Number(t.discountAmount) > 0 ? Number(t.discountAmount) : 0,
        unitPrice: Number(t.unitPrice) > 0 ? Number(t.unitPrice) : null,
        description: String(t.description || '').trim().slice(0, 200),
      };
    })
    .sort((a, b) => a.minEntries - b.minEntries);
}

/**
 * Resolves the matching bulk discount tier for an event given an entry count.
 * @param {Array<{ minEntries: number, maxEntries?: number|null, discountPercent?: number, discountAmount?: number, unitPrice?: number, discountType?: string, description?: string }>} bulkDiscountTiers
 * @param {number} entryCount
 * @returns {object|null}
 */
export function resolveApplicableBulkDiscountTier(bulkDiscountTiers, entryCount) {
  if (!Array.isArray(bulkDiscountTiers) || !bulkDiscountTiers.length || Number(entryCount) <= 0) {
    return null;
  }
  const count = Number(entryCount);
  const validTiers = bulkDiscountTiers
    .filter((t) => t && Number(t.minEntries) >= 1 && (Number(t.discountPercent) > 0 || Number(t.discountAmount) > 0 || Number(t.unitPrice) > 0))
    .sort((a, b) => a.minEntries - b.minEntries);

  let bestMatch = null;
  for (const tier of validTiers) {
    const min = Number(tier.minEntries);
    const max = tier.maxEntries != null && tier.maxEntries !== '' ? Number(tier.maxEntries) : Infinity;
    if (count >= min && count <= max) {
      bestMatch = tier;
    }
  }
  return bestMatch;
}

/**
 * Returns formatted range label for a volume tier.
 * @param {{ minEntries: number, maxEntries?: number|null }} tier
 * @returns {string}
 */
export function formatBulkTierRange(tier) {
  if (!tier) return '';
  const min = tier.minEntries || 1;
  const max = tier.maxEntries;
  if (max != null && max !== '' && !Number.isNaN(Number(max))) {
    if (Number(max) === Number(min)) return `${min} entry`;
    return `${min} - ${max} entries`;
  }
  return `${min}+ entries`;
}
